import {
  Box,
  LinearProgress,
  Alert,
  Tab,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import React, { useContext, useState } from 'react';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CreateOrEditDriver from '../drivers/driver-forms/create-or-edit-driver';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ClearIcon from '@mui/icons-material/Clear';
import { WhatsAppConnectionQRCode } from './whatsapp-connect';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { WhapiChannelScope } from '../../../interfaces/delivery-company/whapi';
import { IconWithText } from '../../common/styles/components';
import DriverPickups from './driver-pickups';
import DriverDeliveries from './driver-deliveries';
import { Key, Password } from '@mui/icons-material';
import { WhatsAppConnectionAuthCode } from '../../common/whatsapp-auth-code/whatsapp-auth-code';
import DriverOrders from './driver-orders';
import TabsLayout from '../../common/tabs-layout/tabs-layout';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../utils/permissions';
import PasswordDisplayDialog from '../../common/password-display-dialog/password-display-dialog';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { useSnackBar } from '../../../contexts/snackbar';
import DriverLocationMap from './driver-location-map';
import MapIcon from '@mui/icons-material/Map';

function Driver() {
  const { driverId } = useParams();

  const [gettingQRCode, setGettingQRCode] = React.useState(false);
  const [gettingAuthCode, setGettingAuthCode] = React.useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [driver, setDriver] = useState<DriverType>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [confirmChangePassword, setConfirmChangePassword] =
    useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const {showMessage} = useSnackBar()

  const { user, config } = useContext(AuthContext)
  const canChangeDriver = user_has_data_model_access(user, DataResources.DRIVER, ResourceActionType.UPDATE)

  React.useEffect(() => {
    const getAndSetDriver = async () => {
      if (driverId !== undefined) {
        setIsLoading(true);
        try {
          const endpoint = `${API_ENDPOINT}/api/v1/driver/${driverId}/`;

          console.log(endpoint);
          const response = await axios.get<DriverType>(endpoint, config);
          console.log(response);
          console.log('driver response =', response);
          setResponse(response);
          setDriver(response.data);
        } catch (e) {
          setDriver(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load driver data: ' + e.message);
          } else {
            setMessage('Failed to load driver data');
          }
          console.log(e);
        }
        setIsLoading(false);
      }
    };
    getAndSetDriver();
  }, []);

  const changePassword = async () => {
    if(driver && driver.id !== '') {
      setIsLoading(true);
      setResponse(undefined);

      try {
        const endpoint = `${API_ENDPOINT}/api/v1/drivers/${driver.id}/change-password/`;

        const response = await axios.post(endpoint,{}, config);
        setResponse(response);
        showMessage("Driver password changed successfully", "success")
        // Show password dialog on success
        if (response.data && response.data.password) {
          setNewPassword(response.data.password);
          setShowPasswordDialog(true);
        }
      } catch (e) {
        showMessage("Failed to change driver password", "error")
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
      }}
      setIsLoading(false);
    }
  }


  return (
    <div>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3, marginBottom: 0 }}
      >
        Driver
      </Typography>

      {driver && (
        <Typography
          variant="h5"
          component="h5"
          sx={{ textAlign: 'center', mt: 3, mb: 3, marginTop: 0 }}
        >
          {driver.name}
        </Typography>
      )}

      <Box>
        {isLoading && <LinearProgress />}

        {message !== '' && (
          <Alert
            severity={
              response && response.status >= 200 && response.status < 300
                ? 'success'
                : 'error'
            }
          >
            {message}
          </Alert>
        )}

        {!isLoading && driver && (
          <>
            <TabsLayout
              source="driver-page-layout"
              orientation='horizontal'
              sections={[
                {
                  icon: <InventoryIcon color="primary" />,
                  label: "Orders",
                  content: <DriverOrders driver={driver} />,
                }, 
                {
                  icon: <ListAltIcon color="primary" />,
                  label: "Pickups",
                  content: <DriverPickups driver={driver} />
                }, 
                {
                  icon: <LocalShippingIcon color="primary" />,
                  label: "Delivery Batches",
                  content: <DriverDeliveries driver={driver} />
                }, 
                {
                  icon: <MapIcon color="primary" />,
                  label: "Location",
                  content: <DriverLocationMap driver={driver} />
                },
                {
                  icon: <AccountBoxIcon color="primary" />,
                  label: "Account",
                  content: <CreateOrEditDriver 
                    defaultDriver={driver} 
                    headless={true}
                    onConnectQRCode={() => setGettingQRCode(true)}
                    onConnectPINCode={() => setGettingAuthCode(true)}
                    onChangePassword={() => setConfirmChangePassword(true)}
                    canChangeDriver={canChangeDriver}
                  />
                }, 
              ]}
            />
          </>
        )}
      </Box>

      {
        <CustomDialog
          open={gettingQRCode}
          onClose={() => setGettingQRCode(false)}
        >
          <div
            style={{
              textAlign: 'right',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <DialogTitle>Connecting to Whatsapp Integration</DialogTitle>
            <IconButton
              onClick={() => {
                setGettingQRCode(false);
              }}
            >
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>

          <DialogContent>
            <WhatsAppConnectionQRCode scope={WhapiChannelScope.DRIVER} />
          </DialogContent>
        </CustomDialog>
      }

      <ConfirmAction
          message={'Are you sure you want to generate a new password for this Driver?'}
          action={changePassword}
          isOpen={confirmChangePassword}
          handleClose={() => setConfirmChangePassword(false)}
        >
          <Alert severity='warning'>Previous passwords will become inactive.</Alert>
      </ConfirmAction>

      {
         <PasswordDisplayDialog
         open={showPasswordDialog}
         onClose={() => setShowPasswordDialog(false)}
         password={newPassword}
       />
      }

      {driver && gettingAuthCode && (
        <CustomDialog
          open={gettingAuthCode}
          onClose={() => setGettingAuthCode(false)}
        >
          <div
            style={{
              textAlign: 'right',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <DialogTitle>Connecting to Whatsapp Integration</DialogTitle>
            <IconButton
              onClick={() => {
                setGettingAuthCode(false);
              }}
            >
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>

          <DialogContent>
            <WhatsAppConnectionAuthCode driver={driver} />
          </DialogContent>
        </CustomDialog>
      )}
    </div>
  );
}

export default Driver;
